import { useAppTranslation } from 'src/i18n/hooks';
import './styles.scss';
import { Modal, notification, Image } from 'antd';
import { useRef } from 'react';
import html2canvas from 'html2canvas-pro';
import { getTierImage, getTierNameByPoint } from '..';
import { Button } from 'src/components/ui/button';
import { XIcon } from 'src/assets/icons';
import { CheckCircleIcon } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
  points: number;
  totalPoints: number;
}

const CelebrationModal = ({ points, totalPoints, onClose, open }: Props) => {
  const { t } = useAppTranslation('leaderboard');
  const cardRef = useRef<HTMLDivElement>(null);

  const handleShareAndTweet = async () => {
    if (cardRef.current) {
      const canvas = await html2canvas(cardRef.current);
      const imageData = canvas.toDataURL('image/png');

      // Convert to blob for sharing
      const blob = await (await fetch(imageData)).blob();

      // Check if device supports native sharing (mobile devices)
      const tweetText = `My total point on vdex is ${totalPoints}`;
      if (
        navigator.share &&
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      ) {
        try {
          const file = new File([blob], 'vdex-position.png', { type: 'image/png' });
          await navigator.share({
            text: tweetText,
            files: [file],
          });
          return;
        } catch (shareError) {
          console.log('Native share failed, falling back to clipboard + Twitter');
        }
      }

      try {
        // Try to copy image to clipboard (modern browsers)
        if (navigator.clipboard && window.ClipboardItem) {
          const clipboardItem = new ClipboardItem({
            'image/png': blob,
          });
          await navigator.clipboard.write([clipboardItem]);

          // Show success notification
          notification.success({
            message: t('imageCopiedToClipboard', { ns: 'trade' }),
            description: t('pasteImageInTweet', { ns: 'trade' }),
            placement: 'bottomRight',
            duration: 15, //15s
          });
        } else {
          // Fallback: download for older browsers
          const downloadLink = document.createElement('a');
          downloadLink.href = imageData;
          downloadLink.download = 'vdex-point.png';
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);

          notification.info({
            message: t('imageDownloaded', { ns: 'trade' }),
            description: t('attachImageToTweet', { ns: 'trade' }),
            placement: 'bottomRight',
            duration: 15, //15s
          });
        }
      } catch (clipboardError) {
        console.log('Clipboard failed, falling back to download');
        // Fallback to download
        const downloadLink = document.createElement('a');
        downloadLink.href = imageData;
        downloadLink.download = 'vdex-point.png';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        notification.info({
          message: t('imageDownloaded', { ns: 'trade' }),
          description: t('attachImageToTweet', { ns: 'trade' }),
          placement: 'bottomRight',
          duration: 15, //15s
        });
      }

      // Open Twitter with text
      setTimeout(() => {
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
        window.open(twitterUrl, '_blank');
      }, 1000);
    }
  };

  const copyImage = async () => {
    if (cardRef.current) {
      const canvas = await html2canvas(cardRef.current);
      const imageData = canvas.toDataURL('image/png');

      // Convert to blob for sharing
      const blob = await (await fetch(imageData)).blob();
      try {
        if (navigator.clipboard && window.ClipboardItem) {
          const clipboardItem = new ClipboardItem({
            'image/png': blob,
          });
          await navigator.clipboard.write([clipboardItem]);

          // Show success notification
          notification.success({
            message: t('imageCopiedToClipboard', { ns: 'trade' }),
            placement: 'bottomRight',
            duration: 5,
          });
        } else {
          // Fallback: download for older browsers
          const downloadLink = document.createElement('a');
          downloadLink.href = imageData;
          downloadLink.download = 'vdex-point.png';
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);

          notification.success({
            message: t('browserNotSupportCopyImage', { ns: 'trade' }),
            placement: 'bottomRight',
            duration: 5, //5s
          });
        }
      } catch {
        // Fallback: download for older browsers
        const downloadLink = document.createElement('a');
        downloadLink.href = imageData;
        downloadLink.download = 'vdex-point.png';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        notification.success({
          message: t('browserNotSupportCopyImage', { ns: 'trade' }),
          placement: 'bottomRight',
          duration: 5, //5s
        });
      }
    }
  };

  const captureImage = async () => {
    if (cardRef.current) {
      try {
        const canvas = await html2canvas(cardRef.current);
        const imageData = canvas.toDataURL('image/png');

        const blob = await (await fetch(imageData)).blob();
        const blobUrl = URL.createObjectURL(blob);

        const downloadLink = document.createElement('a');
        downloadLink.href = blobUrl;
        downloadLink.download = 'vdex-point.png';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        URL.revokeObjectURL(blobUrl);

        return imageData;
      } catch (error) {
        console.error('Error capturing image:', error);
        return null;
      }
    }
    return null;
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      centered
      className="[&_.ant-modal-content]:p-[20px]"
      title={null}
      footer={null}
      closeIcon={null}
    >
      <div
        ref={cardRef}
        style={{
          backgroundColor: '#101116',
        }}
        className="bg-cover pnl-card flex flex-col px-[20px] pb-[20px] pt-[15px] mobilePnl:pt-[25px] mobilePnl:pb-[0px] text-white shadow-l"
      >
        <div className="">
          <div className="flex justify-center w-full">
            <Image preview={false} width={180} height={40} src={'/images/vdex-logo-v2.png'} />
          </div>
          <div className="mb-6 flex items-center justify-center mt-[20px]">
            <div className="text-green-400">
              <CheckCircleIcon className="w-6 h-6" />
            </div>
            <span className="ml-2 text-sm font-bold uppercase tracking-widest text-green-400">
              {t('claimSuccessful')}
            </span>
          </div>
          <div className="relative mb-8 flex items-center justify-center flex-col">
            <h1 className="text-7xl md:text-8xl font-black text-[#8B5CF6] text-neon filter">
              {points}
            </h1>
            <p className="text-gray-500 dark:text-gray-400 font-medium text-lg mt-2 uppercase tracking-wide">
              {t('pointClaimed')}
            </p>
          </div>

          <div className="w-full bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 flex items-center justify-between border border-gray-700/50 mb-8 box-neon">
            <div className="flex flex-col items-start">
              <span className="text-xs text-gray-400 uppercase font-semibold">
                {t('totalPoints')}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-white">
                  {Number(totalPoints ?? 0).toLocaleString('en-US', {
                    maximumFractionDigits: 4,
                    minimumFractionDigits: 0,
                  })}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3 pl-4 border-l border-gray-300 dark:border-gray-600">
              <div className="text-right">
                <p className="text-xs text-gray-500 dark:text-gray-400">Current Tier</p>
                <p className="text-sm font-bold text-[#3B82F6]">
                  {getTierNameByPoint(totalPoints ?? 0)}
                </p>
              </div>
              <div className="relative w-12 h-12 rounded-full p-[2px]">
                <div className="w-full h-full rounded-full overflow-hidden relative">
                  <img
                    alt="Clownfish Tier Avatar"
                    className="w-full h-full object-cover"
                    src={getTierImage(getTierNameByPoint(totalPoints ?? 0))}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-[10px] mt-[16px]">
        <Button
          className="bg-gradient-to-tr from-[#0f1fd0] via-[#7110e4] via-[#6f10e4] to-[#c403f6] hover:opacity-[0.9] gap-[2px] text-white"
          onClick={handleShareAndTweet}
        >
          <Image preview={false} width={18} height={18} src={XIcon} />
          <span>{t('tweet', { ns: 'trade' })}</span>
        </Button>
        <div className="flex gap-[10px]">
          <Button
            className="flex-1 p-[1px] rounded-[6px] bg-gradient-to-r from-[#0f1fd0] to-[#c403f6]  hover:bg-[#312f39] text-white"
            onClick={copyImage}
          >
            <div className="bg-black rounded-[6px] h-[100%] w-full hover:opacity-[0.9] flex items-center justify-center">
              {t('copyImage', { ns: 'trade' })}
            </div>
          </Button>
          <Button
            className="flex-1 p-[1px] rounded-[6px] bg-gradient-to-r from-[#0f1fd0] to-[#c403f6]  hover:bg-[#312f39] text-white"
            onClick={captureImage}
          >
            <div className="bg-black rounded-[6px] h-[100%] w-full hover:opacity-[0.9] flex items-center justify-center">
              {t('download', { ns: 'trade' })}
            </div>
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CelebrationModal;
